import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, message, Card, Typography, Popconfirm, ColorPicker } from 'antd';
import { ExperimentOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface Material {
  id: number;
  name: string;
  color: string;
  max_temperature: number;
  min_temperature: number;
}

const MaterialManagement: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/materials', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      }
    } catch (error) {
      console.error('获取材料列表失败:', error);
      message.error('获取材料列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMaterial(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (material: Material) => {
    setEditingMaterial(material);
    form.setFieldsValue({
      ...material,
      color: material.color || '#1890ff'
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/materials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        message.success('删除成功');
        fetchMaterials();
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '删除失败');
      }
    } catch (error) {
      console.error('删除材料失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('admin_token');
      
      let response;
      if (editingMaterial) {
        // 更新材料
        response = await fetch(`/api/admin/materials/${editingMaterial.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      } else {
        // 创建材料
        response = await fetch('/api/admin/materials', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      }
      
      if (response.ok) {
        message.success(editingMaterial ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchMaterials();
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '操作失败');
      }
    } catch (error) {
      console.error('保存材料失败:', error);
      message.error('保存失败');
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <ExperimentOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => (
        <Space>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: color || '#1890ff',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }} />
          <Text>{color || '#1890ff'}</Text>
        </Space>
      ),
    },
    {
      title: '最高温度',
      dataIndex: 'max_temperature',
      key: 'max_temperature',
      render: (temp: number) => `${temp}°C`,
    },
    {
      title: '最低温度',
      dataIndex: 'min_temperature',
      key: 'min_temperature',
      render: (temp: number) => `${temp}°C`,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Material) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个材料吗？"
            description="如果该材料已被打印机使用，将无法删除。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4}>材料管理</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加材料
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={materials}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingMaterial ? '编辑材料' : '添加材料'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ 
            max_temperature: 250,
            min_temperature: 180,
            color: '#1890ff'
          }}
        >
          <Form.Item
            name="name"
            label="材料名称"
            rules={[{ required: true, message: '请输入材料名称' }]}
          >
            <Input placeholder="例如：PLA、ABS、PETG" />
          </Form.Item>
          
          <Form.Item
            name="color"
            label="颜色"
            rules={[{ required: true, message: '请选择颜色' }]}
          >
            <ColorPicker
              format="hex"
              showText
              presets={[
                {
                  label: '推荐颜色',
                  colors: [
                    '#1890ff',
                    '#52c41a',
                    '#faad14',
                    '#f5222d',
                    '#722ed1',
                    '#13c2c2',
                    '#eb2f96',
                    '#fa8c16',
                  ],
                },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="max_temperature"
            label="最高温度"
            rules={[{ required: true, message: '请输入最高温度' }]}
          >
            <InputNumber
              min={0}
              max={500}
              step={5}
              style={{ width: '100%' }}
              placeholder="例如：250"
              addonAfter="°C"
            />
          </Form.Item>
          
          <Form.Item
            name="min_temperature"
            label="最低温度"
            rules={[{ required: true, message: '请输入最低温度' }]}
          >
            <InputNumber
              min={0}
              max={500}
              step={5}
              style={{ width: '100%' }}
              placeholder="例如：180"
              addonAfter="°C"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MaterialManagement;
